import { Component, OnInit, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { AppComponent } from '../app.component';
import { text } from '../global.constants';

// Mirror of ASTRO_PREFIXES / ASTRO_EXACT in worker/router.js.
// When adding a new marketing section, update ALL THREE places:
//   1. This file (ASTRO_PREFIXES / ASTRO_EXACT below)
//   2. worker/router.js  (ASTRO_PREFIXES / ASTRO_EXACT)
//   3. frontend/ngsw-config.json  (navigationUrls exclusion patterns, e.g. !/releases/**)
// Keeping them in sync ensures the Cloudflare Worker, the Angular service worker,
// and this iframe fallback all agree on which paths belong to the Astro site.
const ASTRO_PREFIXES = [
  '/features/',
  '/pricing',
  '/resources/',
  '/compare/',
  '/training/',
  '/get-started',
  '/signup',
  '/about',
  '/playbook',
  '/how-it-works',
  '/privacy',
  '/terms',
  '/brand',
  '/internal/',
  '/lp/',
  '/docs/',
  '/releases/',
  '/og-preview',
  '/og/',
  '/sitemap',
  '/robots.txt',
];
const ASTRO_EXACT = new Set(['/', '/404', '/releases']);

function isAstroPath(pathname: string): boolean {
  if (ASTRO_EXACT.has(pathname)) return true;
  return ASTRO_PREFIXES.some(p => pathname.startsWith(p));
}

@Component({
  selector: 'app-notfound',
  templateUrl: './app.notfound.component.html',
  // :host rules must stay here (component-scoped).
  // .astro-embed and .not-found-content live in _exception.scss.
  styles: [`
    :host {
      display: block;
      /* Side margins cancel layout-content's 36px side padding so the
         iframe sits flush to the shell edges. No vertical margin tricks —
         we strip layout-content padding via JS instead. */
      margin: 0 -36px;
    }
    @media (max-width: 991px) {
      :host { margin: 0 -14px; }
    }
  `],
})
export class AppNotfoundComponent implements OnInit, OnDestroy {
  company = text.companyName;
  project = text.projectName;

  /** True when the current URL belongs to the Astro marketing site. */
  isAstroPage = false;

  /** Sanitised src for the embed iframe. */
  iframeSrc: SafeResourceUrl | null = null;

  // In local dev the Cloudflare Worker isn't running, so same-origin /releases/
  // paths would hit Angular again. Point at the local Astro dev server instead.
  private readonly astroBase =
    window.location.hostname === 'localhost' ? 'http://localhost:4321' : '';

  // We strip layout-content's padding while the iframe is shown so there's
  // no gap around the frame and no double scrollbar. Restored on destroy.
  private layoutContent: HTMLElement | null = null;
  private prevPadding = '';
  private prevOverflow = '';

  // Sync iframe when the user navigates back/forward via browser history.
  private readonly onPopState = () => {
    const next = window.location.pathname + window.location.search;
    this.setIframeSrc(next);
  };

  private readonly onMessage = (event: MessageEvent) => {
    // Origin guard — only accept messages from the Astro site.
    // In production the iframe is same-origin (moveright.app); in local dev it
    // comes from the Astro dev server at localhost:4321.
    const allowedOrigins = [window.location.origin, 'http://localhost:4321'];
    if (!allowedOrigins.includes(event.origin)) return;

    if (!event.data) return;
    const { type, url } = event.data as { type: string; url: string };
    if (typeof url !== 'string' || !url.startsWith('/')) return;

    if (type === 'mr:app-navigate') {
      // Quick-link from the embedded 404 page — navigate Angular router directly.
      this.router.navigateByUrl(url);
      return;
    }

    if (type === 'mr:navigate') {
      // Link click inside an Astro page — update address bar + reload iframe.
      const displayUrl = url.length > 1 ? url.replace(/\/$/, '') : url;
      history.pushState(null, '', displayUrl);
      this.setIframeSrc(url);
    }
  };

  constructor(public app: AppComponent, private sanitizer: DomSanitizer, private router: Router) {}

  ngOnInit(): void {
    const path = window.location.pathname;
    if (isAstroPath(path)) {
      this.isAstroPage = true;
      this.setIframeSrc(path);
      window.addEventListener('message', this.onMessage);
      window.addEventListener('popstate', this.onPopState);

      this.layoutContent = document.querySelector<HTMLElement>('.layout-content');
      if (this.layoutContent) {
        this.prevPadding = this.layoutContent.style.padding;
        this.prevOverflow = this.layoutContent.style.overflow;
        this.layoutContent.style.padding = '0';
        this.layoutContent.style.overflow = 'hidden';
      }
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.onMessage);
    window.removeEventListener('popstate', this.onPopState);
    if (this.layoutContent) {
      this.layoutContent.style.padding = this.prevPadding;
      this.layoutContent.style.overflow = this.prevOverflow;
      this.layoutContent = null;
    }
  }

  private setIframeSrc(path: string): void {
    const [pathname, qs] = path.split('?');
    // Don't append trailing slash to file-like paths (e.g. /robots.txt, /sitemap.xml).
    const isFileLike = /\/[^/]+\.[^/]+$/.test(pathname);
    const normalised = pathname.endsWith('/') || isFileLike ? pathname : pathname + '/';
    const query = qs ? `?${qs}&stripHeader` : '?stripHeader';
    const url = this.astroBase + normalised + query;
    this.iframeSrc = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
